import { useEffect, useState } from 'react';
import { useParams, useSearchParams } from 'react-router-dom';
import { Layout, Loading, ErrorDisplay } from '../components/Layout';
import { fetchPublicConfig, ApiError } from '../services/api';
import { buildRedirectUrl, performRedirect } from '../services/redirect';
import { getClubCodeFromCookie, normalizeClubCode } from '../utils/cookie';
import { logFlowStep, logError, logRedirect } from '../services/logger';

const Public = () => {
  const { keyword } = useParams<{ keyword: string }>();
  const [searchParams] = useSearchParams();
  const [status, setStatus] = useState<string>('Loading...');
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    const processRedirect = async () => {
      try {
        logFlowStep('public', 'start');

        // Validate keyword
        if (!keyword) {
          logError('ValidationError', 'Missing link keyword in URL');
          setError('Missing link keyword in URL');
          return;
        }

        setStatus('Retrieving club information...');

        // Get clubCode from query param or cookie
        let clubCode: string | null = searchParams.get('clubCode');
        let clubCodeSource: 'query' | 'cookie' = 'query';
        if (!clubCode) {
          clubCode = getClubCodeFromCookie() || null;
          clubCodeSource = 'cookie';
        }

        if (!clubCode) {
          logError('ValidationError', 'Missing club code');
          setError('Missing club code. Please provide clubCode in the URL or ensure you have the ace-zipdata cookie set.');
          return;
        }

        // Normalize clubCode (strip leading zeros)
        const normalizedClubCode = normalizeClubCode(clubCode);
        logFlowStep('public', 'club_code_found', { source: clubCodeSource, clubCode: normalizedClubCode });

        setStatus('Fetching redirect configuration...');
        logFlowStep('public', 'fetch_config', { keyword });

        // Fetch config from API
        const config = await fetchPublicConfig(keyword, normalizedClubCode);

        logFlowStep('public', 'config_received', { keyword, alpid: config.alpid, clubCode: normalizedClubCode });
        setStatus('Redirecting...');

        // Build and perform redirect
        const redirectUrl = buildRedirectUrl(config, { clubCode: normalizedClubCode });
        logRedirect(keyword, config.alpid, normalizedClubCode);
        performRedirect(redirectUrl);
      } catch (err) {
        const errorMessage = err instanceof Error ? err.message : 'An unexpected error occurred';
        logError('PublicRedirectError', errorMessage);

        if (err instanceof ApiError) {
          if (err.isForbidden) {
            setError('This link requires authentication. Please use the authenticated link instead.');
          } else if (err.isNotFound) {
            setError('Link not found. Please check the URL and try again.');
          } else {
            setError(err.message || 'An error occurred');
          }
        } else {
          setError(errorMessage);
        }
      }
    };

    processRedirect();
  }, [keyword, searchParams]);

  return (
    <Layout>
      {error ? (
        <ErrorDisplay message={error} onRetry={() => window.location.reload()} />
      ) : (
        <Loading message={status} />
      )}
    </Layout>
  );
};

export default Public;
